import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { LoginForm } from '@/components/Auth/LoginForm';
import { SplashScreen } from '@/components/Splash/SplashScreen';
import { WelcomeScreen } from '@/components/Splash/WelcomeScreen';
import LoadingSpinner from './components/LoadingComponent';
import { ThemeProvider, ThemeToggle } from '@/lib/ThemeProvider';
import { Button } from '@/components/ui/button';
import { SubscribePage } from '@/components/Subscribe/SubscribePage';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { TermsAndPolicyPage } from '@/components/Terms/TermsAndPolicyPage';
import { ActivatePage } from '@/components/Activate/ActivatePage';
import { motion, AnimatePresence } from 'framer-motion';
import { Footer } from './components/ui/footer';
import { RecommendationSlider } from '@/components/ui/RecommendationSlider';
import { StreaksPage } from '@/components/Streaks/StreaksPage';
import { MaintenancePage } from '@/components/Streaks/MaintenancePage';
import { Toaster } from 'sonner';
import LeadsPage from '@/components/Leads/Leads';
import { ErrorFAQPage } from './pages/ErrorFAQPage';
import NewsTicket from './components/NewsTicket/NewsTicket';
import { DeliveryForm } from './pages/Forms/DeliveryForm';
import EnhancedHome from './pages/Landing/Final';
import { UrlRedirect } from '@/components/Url/UrlRedirect';
import { RestartPage } from './pages/RestartPage';
import { SupportForm } from './pages/Forms/SupportForm';
import { FeedbackForm } from './pages/Forms/FeedbackForm';
import { CEOForm } from './pages/Forms/CEOForm';
import { StreakForm } from './pages/Forms/StreakForm';
import { ImageUploadForm } from './pages/Forms/ImageUploadForm';
import { ImageGalleryPage } from './pages/Forms/ImageGalleryPage';
import { PendingEmailsSection } from '@/components/Referrals/PendingEmailsSection';
import { ResendModal } from '@/components/Referrals/ResendModal';
import { RewardsSection } from '@/components/Referrals/RewardsSection';

// Tipos para os novos dados da API
interface Referral {
  referral_id: number;
  referred_email: string;
  created_at: string;
  resend_count: number;
}

interface ReferralData {
  email: string;
  total_pending: number;
  total_active: number;
  pending_referrals: Referral[];
  active_referrals: Referral[];
}

// Variantes de animação para transições de página
const pageVariants = {
  initial: { opacity: 0, x: 20 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// Componente de wrapper para animar as transições de página
const AnimatedRoute = ({ children }: { children: JSX.Element }) => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait"> {/* Use mode="wait" para evitar flicker */}
      <motion.div
        key={location.pathname} // Use location.pathname como key para cada rota
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.5, ease: "easeInOut" }} // Transição suave
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

// Componente de redirecionamento para WhatsApp
const WhatsAppRedirect = () => {
  useEffect(() => {
    window.location.href = "https://api.whatsapp.com/send?phone=5511994880433&text=Ol%C3%A1!%0AQuero%20confirmar%20a%20minha%20presen%C3%A7a%20para%20o%20evento%20the%20talks%20dia%2026%2F03";
  }, []);

  return <LoadingSpinner />;
};

const AheadRedirect = () => {
  useEffect(() => {
    window.location.href = "https://whatsapp.com/channel/0029VbA4JseLCoX6hA9IFI1x";
  }, []);

  return <LoadingSpinner />;
};

const AppRoutes = () => {
  const location = useLocation();
  const { user, loading, login, logout } = useAuth();
  const [copied, setCopied] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [showWelcome, setShowWelcome] = useState(true);
  const [demographicsConsent, setDemographicsConsent] = useState(false);
  const [referralCounts, setReferralCounts] = useState({
    pending: 0,
    active: 0
  });
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<Record<string, { submitted: boolean; submittedAt: number | null }>>({});
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Verifica se está na rota de redirecionamento
  const isRedirectRoute = location.pathname === '/redirect';

  const referralLink = `https://thenewscc.com.br/?ref=${user?.referrer_id}`;
  
  const rewards = [
    { 
      target: 1, 
      prize: "recap mensal",
      description: "receba todo mês uma edição exclusiva do the news com o resumo dos acontecimentos mais relevantes",
      image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/recap%20mensal.png",
      id: null,
      achieved: referralCounts.active >= 1
    },
    { 
      target: 5, 
      prize: "adesivos",
      description: "indique e ganhe o novo pack de adesivos do the news",
      image: "https://app.thenewscc.com.br/adesivos.png",
      id: '1dcfa7e4bfff2d1a67076fde7e2e293f5de37187715082a1195469283a44c03c',
      achieved: referralCounts.active >= 5
    },
    { 
      target: 15, 
      prize: "imã marcador de página", 
      description: "perfeito para deixar sua leitura mais prática e charmosa.",
      image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/Screenshot%202025-04-01%20at%2012.16.46%E2%80%AFAM.png",
      id: 'de89f476871268f9234ac86d5973af61bc7f15acc8a686f45d4ee77992bc57fa',
      achieved: referralCounts.active >= 15
    },
    {
      target: 30,
      prize: "meia",
      description: "ganhe um par de meias do dênius.",
      image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/meia.png",
      id: '37c5bbe4f17965a8e4df866b6d9ae2258ee8b583d64c27b2574803985825f0e9',
      achieved: referralCounts.active >= 30
    },
    { 
      target: 60, 
      prize: "moleskine", 
      description: "Indique amigos e ganhe o moleskine do the news",
      image: "https://app.thenewscc.com.br/moleskine.png",
      id: 'd950113d83977940d0160cdc6f59edd3da64ac03b4f7cd896a27921a45cd8fb4',
      achieved: referralCounts.active >= 60
    },
    { 
      target: 100, 
      prize: "caneca", 
      description: "ganhe a caneca mais cobiçada do país",
      image: "https://app.thenewscc.com.br/caneca.png",
      id: '8923cac0414836b7dde6ce693aec4f8dc094093acb2c89f3937774a74830ec09',
      achieved: referralCounts.active >= 100
    },
    { 
      target: 200, 
      prize: "boné", 
      description: "um toque de classe para sua cabeça.",
      image: "https://pub-d4d36832efd647f5a00ae2d9086a65ea.r2.dev/bone%CC%81.png",
      id: 'c8dacf657fe92b6064dbcde0f7888936b2c35871e6c61f7b4581accba92c278d',
      achieved: referralCounts.active >= 200,
      releaseDate: new Date('2025-04-03')
    },
  ];

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStart = (demographicsConsent: boolean) => {
    setShowWelcome(false);
    setDemographicsConsent(demographicsConsent);
  };

  useEffect(() => {
    const fetchReferralCounts = async () => {
      if (user?.email) {
        try {
          const response = await fetch(`https://temp.testeswaffle.org/referrals/count/${encodeURIComponent(user.email)}`);
          const data = await response.json();
          
          setReferralData(data);
          setReferralCounts({
            pending: data.total_pending,
            active: data.total_active
          });
        } catch (error) {
          console.error('Erro ao buscar contagem de referrals:', error);
        }
      }
    };

    fetchReferralCounts();
  }, [user?.email]);

  const handleResendActivation = async (referral: Referral) => {
    setSelectedReferral(referral);
    setShowResendModal(true);
  };

  const confirmResendActivation = async () => {
    if (!selectedReferral) return;
    
    setResendLoading(true);
    try {
      // Capturar token da URL se disponível
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      // Preparar headers da requisição
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      // Adicionar token ao header Authorization se fornecido
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`https://backend.testeswaffle.org/referrals/resend/${selectedReferral.referral_id}`, {
        method: 'POST',
        headers
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Atualizar os dados locais mantendo a data original
        if (referralData) {
          const updatedReferralData = { ...referralData };
          const pendingIndex = updatedReferralData.pending_referrals.findIndex(
            r => r.referral_id === selectedReferral.referral_id
          );
          
          if (pendingIndex !== -1) {
            // Manter a data original e apenas atualizar o resend_count
            updatedReferralData.pending_referrals[pendingIndex] = {
              ...updatedReferralData.pending_referrals[pendingIndex],
              resend_count: data.data.resend_count
            };
            setReferralData(updatedReferralData);
          }
        }
        
        // Fechar modal
        setShowResendModal(false);
        setSelectedReferral(null);
      } else {
        alert(`Erro: ${data.message}`);
      }
    } catch (error) {
      console.error('Erro ao reenviar email de ativação:', error);
      alert('Erro ao reenviar email de ativação. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  };


  useEffect(() => {
    const fetchDeliveryStatus = async () => {
      if (user?.email) {
        try {
          const response = await fetch(`https://temp.testeswaffle.org/referrals/${user.email}/delivery-buttons`);
          const data = await response.json();
          
          if (data.success) {
            setDeliveryStatus(data.products);
          }
        } catch (error) {
          console.error('Erro ao buscar status dos botões de entrega:', error);
        }
      }
    };

    fetchDeliveryStatus();
  }, [user?.email]);

  // Não mostra splash screen se estiver na rota de redirecionamento
  if (loading || (showSplash && !isRedirectRoute)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        {showSplash ? (
          <SplashScreen onComplete={() => setShowSplash(false)} />
        ) : (
          <LoadingSpinner />
        )}
      </div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>

        <Route path="/cases" element={
          <AnimatedRoute>
            <LeadsPage />
          </AnimatedRoute>
        } />

        <Route path="/subscribe" element={
          <AnimatedRoute>
            <SubscribePage />
          </AnimatedRoute>
        } />
        <Route path="/terms-and-policy" element={
          <AnimatedRoute>
            <TermsAndPolicyPage />
          </AnimatedRoute>
        } />
        <Route path="/activate" element={
          <AnimatedRoute>
            <ActivatePage />
          </AnimatedRoute>
        } />
        <Route path="/login" element={
          <AnimatedRoute>
            <LoginForm onLogin={(email) => login(email, demographicsConsent)} />
          </AnimatedRoute>
        } />
        <Route path="/" element={
          <AnimatedRoute>
            {showSplash ? (
              <SplashScreen onComplete={() => setShowSplash(false)} />
            ) : showWelcome && !user?.referrer_id ? (
              <WelcomeScreen onStart={handleStart} />
            ) : !user?.referrer_id ? (
              <LoginForm onLogin={(email) => login(email, demographicsConsent)} />
            ) : (
              <ThemeProvider defaultTheme="system" storageKey="ui-theme">
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900" style={{ fontFamily: 'Glacial Indifference, sans-serif' }}>
                  {/* New Header Design */}
                  <div className="flex justify-between items-center p-4 dark:bg-transparent">
                    <div className="container max-w-6xl mx-auto p-4 flex items-center">
                      <img
                        src="https://app.thenewscc.com.br/thenews.webp"
                        alt="Logo"
                        className="w-40 rounded-lg"
                      />
                      <div className="flex items-center gap-2 ml-auto">
                        <ThemeToggle />
                        <button 
                          className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center"
                          onClick={logout}
                        >
                          <span className="sr-only">Sair</span>
                          <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H3m0 0l4-4m-4 4l4 4m6 0h6a2 2 0 002-2v-6a2 2 0 00-2-2h-6" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <main className="flex-1">
                    <div className="container max-w-6xl mx-auto p-4">
                      {/* New Referral Link Card Design */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-6">
                        <h2 className="text-2xl font-bold dark:text-white mb-2">Compartilhe o the news</h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          Compartilhe seu link de indicação único com seus amigos, colegas e familiares para acumular indicações.
                        </p>
                        <div className="flex gap-2">
                          <input 
                            type="text"
                            value={referralLink}
                            readOnly
                            className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 rounded text-sm"
                          />
                          <Button
                            onClick={handleCopyLink}
                            className="bg-yellow-400 hover:bg-yellow-500 text-black dark:text-black px-6"
                          >
                            {copied ? 'Copiado!' : 'Copiar'}
                          </Button>
                        </div>
                      </div>

                        {/* Alerta de Fim de Campanha
                        {!localStorage.getItem('alertaCampanhaFechado') && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6 relative">
                            <div className="flex items-start">
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="ml-3 flex-1">
                                <p className="text-sm text-yellow-700 dark:text-yellow-200">
                                  A campanha de 2x termina hoje! Não perca a chance de ganhar recompensas exclusivas.{' '}
                                  <a href="/restart" className="font-medium underline text-yellow-800 dark:text-yellow-100 hover:text-yellow-900 dark:hover:text-yellow-50">
                                    Saiba mais
                                  </a>
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  localStorage.setItem('alertaCampanhaFechado', 'true');
                                  // Força re-render do componente
                                  window.location.reload();
                                }}
                                className="ml-4 flex-shrink-0"
                              >
                                <span className="sr-only">Fechar</span>
                                <svg className="h-5 w-5 text-yellow-500 dark:text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        )} */}

                        {/* Card de Visão Geral */}
                        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 mb-6">
                          <h3 className="text-2xl font-bold dark:text-white mb-2">Visão geral de indicações</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                          As pessoas que você indicou e confirmaram sua indicação e aquelas que se inscreveram, mas ainda não confirmaram seu convite.
                          </p>
                          
                          <div className="flex gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <span className="font-bold dark:text-white">Confirmado</span>
                              <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 rounded">{referralCounts.active}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold dark:text-white">Pendente</span>
                              <span className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-2 py-1 rounded">{referralCounts.pending}</span>
                            </div>
                          </div>

                          <PendingEmailsSection 
                            referralData={referralData}
                            onResendActivation={handleResendActivation}
                          />
                        </div>

                        <RewardsSection 
                          rewards={rewards}
                          referralCounts={referralCounts}
                          deliveryStatus={deliveryStatus}
                        />
                        <RecommendationSlider />
                        <Footer />
                      </div>
                    </main>
                  
                  </div>

                  <ResendModal 
                    isOpen={showResendModal}
                    selectedReferral={selectedReferral}
                    resendLoading={resendLoading}
                    onClose={() => {
                      setShowResendModal(false);
                      setSelectedReferral(null);
                    }}
                    onConfirm={confirmResendActivation}
                  />
                </ThemeProvider>
            )}
          </AnimatedRoute>
        } />
        <Route path="/streaks" element={<StreaksPage />} />
        <Route path="/FAQ" element={<ErrorFAQPage />} />
        <Route path="/waffle/ticket" element={<NewsTicket />} />
        <Route path="/support" element={<SupportForm />} />
        <Route path="/delivery" element={<DeliveryForm />} />
        <Route path="/evento" element={<EnhancedHome />} />
        <Route path="/restart" element={
          <AnimatedRoute>
            <RestartPage />
          </AnimatedRoute>
        } />
        <Route path="/talks" element={
          <AnimatedRoute>
            <WhatsAppRedirect />
          </AnimatedRoute>
        } />
        <Route path="/channel" element={
          <AnimatedRoute>
            <AheadRedirect />
          </AnimatedRoute>
        } />
        {/* Página de Redirecionamento */}
        <Route path="/redirect" element={
          <AnimatedRoute>
            <UrlRedirect />
          </AnimatedRoute>
        } />
        {/* Página de Manutenção */}
        <Route path="/streaks/maintenance" element={
          <AnimatedRoute>
            <MaintenancePage />
          </AnimatedRoute>
        } />
        {/* Formulário de Feedback */}
        <Route path="/feedback" element={
          <AnimatedRoute>
            <FeedbackForm />
          </AnimatedRoute>
        } />
        {/* Formulário de Percepção */}
        <Route path="/forms/perception" element={
          <AnimatedRoute>
            <CEOForm />
          </AnimatedRoute>
        } />
        <Route path="/forms/streak" element={
          <AnimatedRoute>
            <StreakForm />
          </AnimatedRoute>
        } />
        {/* Upload de Imagem */}
        <Route path="/upload/dogs" element={
          <AnimatedRoute>
            <ImageUploadForm />
          </AnimatedRoute>
        } />
        {/* Galeria de Imagens */}
        <Route path="/gallery/dogs" element={
          <AnimatedRoute>
            <ImageGalleryPage />
          </AnimatedRoute>
        } />
      </Routes>
    </AnimatePresence>
  );
};

export default function App() {
  return (
    <Router>
      <Toaster richColors />
      <AppRoutes />
    </Router>
  );
}